import { gsap, Linear } from 'gsap';
import type { RefObject } from 'react';
import { createTimelineSequence } from '../../../util/animation/timeline.utils';

interface ModernCreativityRefs {
  carouselItemsRef: RefObject<Array<HTMLElement> | null>;
  imagesRef: RefObject<Array<HTMLElement> | null>;
  windowWidth: number;
  isInCenterChange: (index: number, isInCenter: boolean) => void;
}

export function setupSliderTimeline(
  timeline: gsap.core.Timeline,
  { carouselItemsRef, windowWidth, isInCenterChange, imagesRef }: ModernCreativityRefs,
): void {
  if (carouselItemsRef?.current) {
    const elements = carouselItemsRef.current.map((item) => item);
    const elementWidth = elements[0].offsetWidth;
    const startPosition = windowWidth / 2 + elementWidth * 0.75;
    const endPosition = -(windowWidth / 2 + elementWidth * 0.75);
    const timelines: Array<gsap.core.Timeline> = [];

    elements.forEach((element, index) => {
      let image;
      if (imagesRef?.current) {
        image = imagesRef.current[index];
      }

      timelines.push(
        getItemTimeline(element, image, {
          startPosition,
          endPosition,
          index,
          isInCenterChange,
        }),
      );
    });

    const distance = Math.abs(endPosition - startPosition);
    const durationPerPixel = 1 / distance;
    const pixelSpacing = 40;
    const timeSpacing = durationPerPixel * (elementWidth + pixelSpacing);

    createTimelineSequence(timelines, timeSpacing, timeline);
  }
}

export function getItemTimeline(
  element: HTMLElement,
  image: HTMLElement | undefined,
  options: {
    startPosition: number;
    endPosition: number;
    index: number;
    isInCenterChange: (index: number, isInCenter: boolean) => void;
  },
): gsap.core.Timeline {
  const timeline = gsap.timeline({
    onUpdate: () => {
      const progress = timeline.progress();
      const isInCenter = progress > 0.35 && progress < 0.65;

      options.isInCenterChange(options.index, isInCenter);
    },
  });

  timeline.fromTo(
    element,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      yoyo: true,
      duration: 0.5,
      repeat: 1,
      ease: 'none',
      immediateRender: true,
    },
    0,
  );

  timeline.fromTo(
    element,
    {
      willChange: 'transform',
      z: 0,
      rotateX: 0,
    },
    {
      willChange: 'transform',
      z: -150,
      rotateX: -30,
      yoyo: true,
      duration: 1,
      ease: 'Power2.easeIn',
      immediateRender: true,
    },
    0,
  );

  timeline.fromTo(
    element,
    {
      x: options.startPosition,
    },
    {
      x: options.endPosition,
      duration: 1,
      ease: Linear.easeNone,
      immediateRender: true,
    },
    0,
  );

  if (image) {
    timeline.fromTo(
      image,
      {
        willChange: 'transform',
        xPercent: -10,
        scale: 1.2,
      },
      {
        willChange: 'transform',
        scale: 1.2,
        xPercent: 10,
        duration: 1,
        ease: 'none',
        immediateRender: true,
      },
      0,
    );
  }

  return timeline;
}
